import LoadingSpinner from 'components/common/loading-spinner';
import Button from 'components/inputs/button';
import Text from 'components/inputs/text';
import ProductPhoto from 'components/products/product-photo';
import { AccessoryProductType } from 'data/types';
import { useSearchAccessoryProductsQuery } from 'features/api/project-api';
import { useState } from 'react';
import { formatUSD } from 'utils/helpers';
import { isSearchableManualAccessoryTerm } from 'utils/manual-accessories';

type AccessoryProductSearchProps = {
  addedProductIds: Set<number>;
  onAdd: (product: AccessoryProductType) => void;
};

const SEARCH_DEBOUNCE_MS = 300;

export default function AccessoryProductSearch({
  addedProductIds,
  onAdd
}: AccessoryProductSearchProps) {
  const [term, setTerm] = useState('');
  const searchable = isSearchableManualAccessoryTerm(term);
  const { currentData, isFetching, isError } = useSearchAccessoryProductsQuery(
    term.trim(),
    { skip: !searchable }
  );
  const results = searchable ? currentData ?? [] : [];

  return (
    <div className='flex flex-col gap-3'>
      <Text
        value={term}
        onChange={setTerm}
        debounce={SEARCH_DEBOUNCE_MS}
        placeholder='Search the Alpine catalog by model number or product ID'
        aria-label='Search accessories'
      />
      {searchable && isFetching && (
        <div className='flex items-center gap-2 text-sm text-gray-500'>
          <LoadingSpinner className='h-4 w-4 animate-spin text-gray-400' />
          Searching...
        </div>
      )}
      {searchable && isError && (
        <span className='text-sm text-red-500'>
          Product search failed. Please try again.
        </span>
      )}
      {searchable && !isFetching && !isError && results.length === 0 && (
        <span className='text-sm text-gray-500'>
          No active accessories match &quot;{term.trim()}&quot;.
        </span>
      )}
      {results.length > 0 && (
        <ul
          aria-label='Accessory search results'
          className='flex max-h-80 flex-col divide-y divide-gray-100 overflow-y-auto rounded-lg border border-gray-200'
        >
          {results.map((product) => {
            const added = addedProductIds.has(product.productId);
            return (
              <li
                key={product.productId}
                className='flex items-center gap-4 p-3'
              >
                <ProductPhoto product={product} size='small' />
                <div className='flex min-w-0 grow flex-col'>
                  <div className='flex flex-wrap items-center gap-x-2'>
                    <a
                      className='font-semibold text-blue-500 hover:text-blue-600'
                      href={`https://alpinehomeair.com${product.productUrl}`}
                      rel='noreferrer'
                      target='_blank'
                    >
                      {product.name}
                    </a>
                    {product.availableStock <= 0 && (
                      <span className='text-sm font-semibold text-red-500'>
                        Out of Stock
                      </span>
                    )}
                  </div>
                  <span className='truncate text-sm text-gray-600'>
                    {product.description}
                  </span>
                </div>
                <span className='shrink-0 font-bold'>
                  {formatUSD(product.price, true)}
                </span>
                <Button
                  text={added ? 'Added' : 'Add'}
                  color={added ? 'gray' : 'primary'}
                  disabled={added}
                  onClick={() => onAdd(product)}
                />
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
